import { Workspace } from "@/components/project/workspace"
import { Button } from "@/components/ui/button"
import { CursorProvider } from "@/contexts/cursor"
import { useOriginProjectRuntime } from "@/hooks/project/use-origin-project-runtime"
import { usePlatform } from "@/platform/context"
import { useAuth } from "@/providers/auth"
import { queries } from "@oblique/shared/zero"
import { useQuery } from "@rocicorp/zero/react"
import { ReactFlowProvider } from "@xyflow/react"
import { Suspense, useEffect } from "react"
import { useNavigate, useParams } from "react-router"

export const ProjectId = () => {
	const { projectId } = useParams<{ projectId: string }>()
	const navigate = useNavigate()
	const platform = usePlatform()
	const { status, initialised } = useAuth()
	const viewportClass =
		platform.kind === "electron"
			? "flex h-full items-center justify-center"
			: "flex h-screen items-center justify-center"

	const [project, projectStatus] = useQuery(
		queries.projects.byId(projectId ?? ""),
	)
	useOriginProjectRuntime({
		enabled: initialised && status === "authenticated",
		projectID: projectId,
	})

	// Require auth for editing; redirect to login when ready
	useEffect(() => {
		if (!initialised) return
		if (status === "unauthenticated") {
			navigate(
				`/login?redirect=${encodeURIComponent(`/projects/${projectId ?? ""}`)}`,
			)
		}
	}, [initialised, status, navigate, projectId])

	useEffect(() => {
		const title = project?.name?.trim()
		if (title) {
			document.title = title
			if (platform.kind === "electron" && projectId) {
				window.dispatchEvent(
					new CustomEvent("origin:project-title", {
						detail: { projectID: projectId, title },
					}),
				)
			}
		}
	}, [platform.kind, project?.name, projectId])

	// Error states
	if (!projectId) {
		return (
			<div className={viewportClass}>
				<p className="text-muted-foreground">Project not specified.</p>
			</div>
		)
	}

	if (projectStatus.type === "error") {
		return (
			<div className={`${viewportClass} flex-col gap-2`}>
				<p className="font-medium">Unable to load project</p>
				<p className="text-sm text-muted-foreground">
					Please return to the dashboard and try again.
				</p>
				<Button variant="outline" onClick={() => navigate("/dashboard")}>
					Back to dashboard
				</Button>
			</div>
		)
	}

	if (projectStatus.type === "unknown" || !project) {
		return (
			<div className={viewportClass}>
				<span className="text-sm text-muted-foreground">Opening project…</span>
			</div>
		)
	}

	// Don't render workspace until auth is known; avoids mutate-with-null-context
	if (!initialised || status === "unauthenticated") {
		return (
			<div className={viewportClass}>
				{!initialised ? "Loading…" : "Redirecting to login…"}
			</div>
		)
	}

	return (
		// TODO: reactflowprovider can be around nodeeditor
		<ReactFlowProvider>
			<CursorProvider>
				<Suspense fallback={<div className={viewportClass}>Loading…</div>}>
					<Workspace project={project} />
				</Suspense>
			</CursorProvider>
		</ReactFlowProvider>
	)
}
